import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { fmtDate, inr } from "@/lib/format";
import { StatusChip, invoiceStatusTone } from "@/components/StatusChip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, CheckCircle2 } from "lucide-react";

export default function Invoices() {
  const [searchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [status, setStatus] = useState(searchParams.get("status") || "all");
  const [search, setSearch] = useState("");
  const [payTarget, setPayTarget] = useState(null);
  const [payMode, setPayMode] = useState("Cash");
  const [payRef, setPayRef] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    const params = {};
    if (status !== "all") params.status = status;
    if (search) params.search = search;
    api.get("/invoices", { params }).then((r) => setInvoices(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [status, search]); // eslint-disable-line react-hooks/exhaustive-deps

  const markPaid = async () => {
    setBusy(true);
    try {
      await api.post(`/invoices/${payTarget.invoice_id}/mark-paid`,
        { payment_mode: payMode, payment_reference: payRef });
      toast.success(`${payTarget.invoice_id} marked as Paid (${payMode})`);
      setPayTarget(null); setPayRef(""); setPayMode("Cash");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="invoices-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Invoices</h1>
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input data-testid="invoice-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search rider or invoice ID" className="pl-9 bg-card border-border" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full sm:w-48 bg-card border-border" data-testid="invoice-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="Unpaid">Unpaid</SelectItem>
            <SelectItem value="Overdue">Overdue</SelectItem>
            <SelectItem value="Paid">Paid</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="card-tactical overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-border">
            <th className="p-3">Invoice</th><th className="p-3">Rider</th><th className="p-3">Billing Week</th>
            <th className="p-3">KM Run</th><th className="p-3">Amount</th><th className="p-3">Status</th>
            <th className="p-3">Raised</th><th className="p-3">Paid On</th><th className="p-3">Action</th>
          </tr></thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.invoice_id} className="border-b border-border/50 hover:bg-slate-50" data-testid={`invoice-row-${inv.invoice_id}`}>
                <td className="p-3 font-mono font-medium">{inv.invoice_id}</td>
                <td className="p-3">{inv.rider_name}<div className="text-xs text-slate-500 font-mono">{inv.rider_id}</div></td>
                <td className="p-3 text-slate-400 text-xs">{fmtDate(inv.week_start)} → {fmtDate(inv.week_end)}</td>
                <td className="p-3 font-mono">{inv.total_km} km</td>
                <td className="p-3 font-mono font-semibold">{inr(inv.amount)}</td>
                <td className="p-3"><StatusChip label={inv.status} tone={invoiceStatusTone(inv.status)} testid={`invoice-status-${inv.invoice_id}`} /></td>
                <td className="p-3 text-slate-400">{fmtDate(inv.date_raised)}</td>
                <td className="p-3 text-slate-400">
                  {fmtDate(inv.date_paid)}
                  {inv.payment_mode && (
                    <div className="text-xs text-slate-500" data-testid={`payment-detail-${inv.invoice_id}`}>
                      {inv.payment_mode}{inv.payment_reference ? ` · ${inv.payment_reference}` : ""}
                      {inv.collected_by ? ` · by ${inv.collected_by}` : ""}
                    </div>
                  )}
                </td>
                <td className="p-3">
                  {inv.status !== "Paid" && (
                    <Button size="sm" variant="outline" className="h-7 text-xs border-emerald-800 text-emerald-300"
                      data-testid={`mark-paid-${inv.invoice_id}`} onClick={() => setPayTarget(inv)}>
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark Paid
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {invoices.length === 0 && (
              <tr><td colSpan={9} className="p-8 text-center text-slate-500" data-testid="invoices-empty">No invoices yet. Invoices are auto-generated weekly on each rider's payment day.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!payTarget} onOpenChange={() => setPayTarget(null)}>
        <DialogContent className="bg-card border-border" data-testid="mark-paid-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">Record Payment — {payTarget?.invoice_id}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-400">
              {payTarget?.rider_name} · <span className="font-mono text-slate-700">{inr(payTarget?.amount)}</span>
            </p>
            <div className="space-y-1.5">
              <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Payment Mode *</label>
              <Select value={payMode} onValueChange={setPayMode}>
                <SelectTrigger className="bg-slate-50 border-border" data-testid="pay-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="UPI">UPI</SelectItem>
                  <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium uppercase tracking-wider text-slate-400">Reference / Transaction ID</label>
              <Input data-testid="pay-ref-input" value={payRef} onChange={(e) => setPayRef(e.target.value)}
                placeholder="e.g. UPI txn no. or receipt no." className="bg-slate-50 border-border font-mono" />
            </div>
            <Button onClick={markPaid} disabled={busy} data-testid="mark-paid-confirm"
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold">
              {busy ? "Saving…" : "Confirm Payment"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
